import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {useUpdateEffect} from 'ahooks';
import {Panel, PanelHeader} from '@Components/panel';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import StockPanel from '@Panels/stock-panel';
import type {ImageData, VideoData} from '@Libraries/add-media-library';
import UserMediaPanel from '@Panels/user-media-panel';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';
import {useAppDispatch} from '@/hooks';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

interface PosterEditorAddSlideshowPanelProps {
  panelId: string;
}

enum SLIDESHOW_OPTIONS {
  DEFAULT = 'default',
  MEDIA = 'media',
  UPLOADS = 'uploads',
}

export default function PosterEditorAddSlidePanel({...props}: PosterEditorAddSlideshowPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const [selectedPanel, setSelectedPanel] = useState(SLIDESHOW_OPTIONS.DEFAULT);
  const dispatch = useAppDispatch();

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useEffect((): void => {
    updateHeightInRedux(selectedPanel);
  }, []);

  if (!activeItem || !activeItem.isSlideshow() || !activeSlideUID) {
    return null;
  }

  const updateHeightInRedux = (type: SLIDESHOW_OPTIONS): void => {
    dispatch(
      updatePanelSizeData({
        sizeDataId: props.panelId,
        sizeData: {
          height: getHeightAccordingToMorphType(type),
        },
      })
    );
  };

  const getHeightAccordingToMorphType = (type: SLIDESHOW_OPTIONS): string => {
    switch (type) {
      case SLIDESHOW_OPTIONS.MEDIA:
      case SLIDESHOW_OPTIONS.UPLOADS:
        return '1000px';
      case SLIDESHOW_OPTIONS.DEFAULT:
      default:
        return '284px';
    }
  };

  const getContent = (): ReactElement => {
    return (
      <ControlledList
        className="spacing-p-b-4 spacing-p-t-1 spacing-p-r-4 spacing-p-l-4 spacing-m-0"
        items={[
          {
            id: 'slideshow-options-text',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            className: 'spacing-m-b-3',
            text: window.i18next.t('pmwjs_text'),
            subText: window.i18next.t('pmwjs_add_text_slide'),
            onClick: (): void => {
              void activeItem.addTextSlide();
              onClose();
            },
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-text" />,
            },
          },
          {
            id: 'slideshow-options-media',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            text: window.i18next.t('pmwjs_media'),
            className: 'spacing-m-b-3',
            subText: window.i18next.t('pmwjs_add_stock_media_slide_2'),
            onClick: (): void => {
              updateHeightInRedux(SLIDESHOW_OPTIONS.MEDIA);
              setSelectedPanel(SLIDESHOW_OPTIONS.MEDIA);
            },
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-media" />,
            },
          },
          {
            id: 'slideshow-options-upload',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            text: window.i18next.t('pmwjs_uploads'),
            subText: window.i18next.t('pmwjs_add_uploaded_media_slide'),
            onClick: (): void => {
              updateHeightInRedux(SLIDESHOW_OPTIONS.UPLOADS);
              setSelectedPanel(SLIDESHOW_OPTIONS.UPLOADS);
            },
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail icon="icon-upload-1" />,
            },
          },
        ]}
      />
    );
  };

  if (selectedPanel === SLIDESHOW_OPTIONS.MEDIA) {
    return (
      <StockPanel
        tabsToShow={[StockPanelTabs.PHOTOS, StockPanelTabs.VIDEOS]}
        panelId={props.panelId}
        shouldLoadDataUrlImage
        onAddSelected={(data: (ImageData | VideoData)[]): void => {
          activeItem.prepareAndAddMediaSlides(data);
        }}
        onClose={onClose}
        onBack={(): void => {
          updateHeightInRedux(SLIDESHOW_OPTIONS.DEFAULT);
          setSelectedPanel(SLIDESHOW_OPTIONS.DEFAULT);
        }}
      />
    );
  }

  if (selectedPanel === SLIDESHOW_OPTIONS.UPLOADS) {
    return (
      <UserMediaPanel
        panelId={props.panelId}
        showVectors={false}
        uploadAcceptedMimetype={'image/jpeg,image/png,image/webp,image/gif,video/quicktime,video/mp4,video/x-m4v,video/*'}
        defaultTab={UploadsMediaType.IMAGE}
        mediaTypesToShow={[UploadsMediaType.IMAGE, UploadsMediaType.VIDEO]}
        shouldLoadDataUrlImage
        onAddSelected={(data: (ImageData | VideoData)[]): void => {
          activeItem.prepareAndAddMediaSlides(data);
          onClose();
        }}
        onAddBrandAsset={(): void => {
          void onAddBrandAssetClickedHandler();
        }}
        onClose={onClose}
        onBack={(): void => {
          updateHeightInRedux(SLIDESHOW_OPTIONS.DEFAULT);
          setSelectedPanel(SLIDESHOW_OPTIONS.DEFAULT);
        }}
      />
    );
  }

  const getPanelHeader = (): ReactElement => {
    return <PanelHeader title={window.i18next.t('pmwjs_add_slide')} onClose={onClose} />;
  };

  return <Panel panelContent={getContent()} panelHeader={getPanelHeader()} />;
}
